'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { faThumbsUp, faThumbsDown, faChevronRight } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { Rating, Star } from '@smastrom/react-rating';
import '@smastrom/react-rating/style.css';
import addReviewAction from '@/app/_lib/actions/AddReviewAction';
import voteReviewHelpfulAction from '@/app/_lib/actions/VoteReviewHelpfulAction';
import FormError from '@/app/_components/FormError';
import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer';
import { useGlobalMessage } from '@/app/_lib/hooks';
import { useReCaptcha } from 'next-recaptcha-v3';
import { useProductContext } from '../_context';

// Shared star styles
const starStyles = {
  itemShapes: Star,
  itemStrokeWidth: 0,
  activeFillColor: '#FCB600',
  inactiveFillColor: '#D1D5DB'
};

// Format review timestamp as relative time description. `now` is passed in
// (never read here): reading the clock during render marks the tree dynamic
// under cacheComponents, which knocks the ENTIRE product page body - schema,
// price, Add to Cart - out of the prerendered HTML for any product that has
// a review (the 2026-08 SEO incident). Only call this with a clock value
// obtained in an effect.
const formatReviewTime = (timestamp, now) => {
  if (!timestamp) return '';
  const reviewDate = new Date(timestamp * 1000); // Unix timestamp to JS Date
  const diffMs = now - reviewDate;
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);
  const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365);
  if (diffYears >= 1) return 'Over a year ago';
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return 'Today';
  if (diffDays < 2) return 'Yesterday';
  if (diffDays <= 7) return 'This week';
  if (diffDays <= 28) return 'A few weeks ago';
  if (diffMonths <= 2) return 'About a month ago';
  if (diffMonths <= 6) return 'A few months ago';
  return 'Several months ago';
};

// Absolute, deterministic date label - safe to prerender. Pinned to en-US/UTC
// so the server HTML and the client hydration pass produce identical text.
const formatReviewDate = timestamp => {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

// Server render / raw HTML: absolute date (also better for SEO than relative
// text). After hydration an effect swaps in the usual relative label.
const ReviewTime = t0 => {
  const $ = _c(5);
  const {
    timestamp
  } = t0;
  let t1;
  if ($[0] !== timestamp) {
    t1 = () => formatReviewDate(timestamp);
    $[0] = timestamp;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [label, setLabel] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== timestamp) {
    t2 = () => {
      setLabel(formatReviewTime(timestamp, Date.now()));
    };
    t3 = [timestamp];
    $[2] = timestamp;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  return label;
};
const Reviews = t0 => {
  const $ = _c(63);
  const {
    sub_product_id,
    sub_product_name,
    sub_product_thumb,
    review_stats,
    initialReviews: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initialReviews = t2;
  const router = useRouter();
  const addGlobalMessage = useGlobalMessage();
  const {
    executeRecaptcha
  } = useReCaptcha();
  const {
    addReviewFormOpen,
    setAddReviewFormOpen
  } = useProductContext();
  const reviews = initialReviews;
  const totalRating = review_stats?.average_rating || 0;
  const reviewCount = review_stats?.review_count || 0;
  const [isPending, setIsPending] = useState(false);
  const [newReviewScore, setNewReviewScore] = useState(0);
  const [reviewErrors, setReviewErrors] = useState(0);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [newReview, setNewReview] = useState(t3);
  let t4;
  if ($[3] !== initialReviews) {
    t4 = () => {
      const counts = {};
      for (const review of initialReviews) {
        if (review.helpful_counts) {
          counts[review.review_id] = review.helpful_counts;
        }
      }
      return counts;
    };
    $[3] = initialReviews;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [helpfulCounts, setHelpfulCounts] = useState(t4);
  const [votingReviewId, setVotingReviewId] = useState(null);
  let t5;
  if ($[5] !== addReviewFormOpen || $[6] !== setAddReviewFormOpen) {
    t5 = () => {
      setAddReviewFormOpen(!addReviewFormOpen);
    };
    $[5] = addReviewFormOpen;
    $[6] = setAddReviewFormOpen;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleAddReviewClick = t5;
  let t6;
  if ($[8] !== setAddReviewFormOpen) {
    t6 = () => {
      setAddReviewFormOpen(false);
    };
    $[8] = setAddReviewFormOpen;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const closeReviewAddForm = t6;
  let t7;
  if ($[10] !== addGlobalMessage || $[11] !== votingReviewId) {
    t7 = async (review_id, vote_type) => {
      if (votingReviewId) {
        return;
      }
      setVotingReviewId(review_id);
      const result = await voteReviewHelpfulAction({
        review_id,
        vote_type
      });
      if (result.success) {
        setHelpfulCounts(prev => ({
          ...prev,
          [review_id]: result.counts
        }));
        addGlobalMessage({
          message: result.message,
          messageType: "success"
        });
      } else {
        addGlobalMessage({
          message: result.message,
          messageType: result.requiresAuth ? "info" : "error"
        });
      }
      setVotingReviewId(null);
    };
    $[10] = addGlobalMessage;
    $[11] = votingReviewId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleVote = t7;
  let t8;
  if ($[13] !== newReview) {
    t8 = event => {
      const updatedReview = {
        ...newReview
      };
      updatedReview[event.target.name] = event.target.value;
      setNewReview(updatedReview);
    };
    $[13] = newReview;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleInputChange = t8;
  let t9;
  if ($[15] !== addGlobalMessage || $[16] !== executeRecaptcha || $[17] !== newReviewScore || $[18] !== router || $[19] !== setAddReviewFormOpen || $[20] !== sub_product_id) {
    t9 = async formData => {
      setIsPending(true);
      setReviewErrors({});
      const recaptchaToken = await executeRecaptcha("add_review");
      if (!recaptchaToken) {
        setReviewErrors({
          general: "Security verification failed. Please try again."
        });
        setIsPending(false);
        return;
      }
      const requestData = {
        sub_product_id,
        user_alias: formData.get("user_alias"),
        email: formData.get("email"),
        review_title: formData.get("review_title"),
        review_score: newReviewScore,
        review: formData.get("review"),
        recaptchaToken
      };
      const addReviewResponse = await addReviewAction(requestData);
      setIsPending(false);
      if (addReviewResponse.success) {
        const message = {
          message: "Your review was added.  Once reviewed it will be published here.",
          messageType: "success"
        };
        addGlobalMessage(message);
        setAddReviewFormOpen(false);
        setNewReview({});
        setNewReviewScore(0);
        router.refresh();
      }
      if (addReviewResponse.errors) {
        setReviewErrors(addReviewResponse.errors);
      }
    };
    $[15] = addGlobalMessage;
    $[16] = executeRecaptcha;
    $[17] = newReviewScore;
    $[18] = router;
    $[19] = setAddReviewFormOpen;
    $[20] = sub_product_id;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleAddReviewSubmit = t9;
  const hasReviews = reviews && reviews.length > 0;
  const hasReviewErrors = Object.values(reviewErrors || {}).some(Boolean);
  const t10 = `pb-2 ${!hasReviews ? "bg-gray-3 rounded-lg p-6" : ""}`;
  const t11 = hasReviews ? "mb-8" : "mb-2";
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <h3 className="text-3xl font-extralight">Reviews</h3>;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== hasReviews || $[24] !== reviewCount || $[25] !== totalRating) {
    t13 = hasReviews && reviewCount > 0 && <div className="flex items-center"><Rating value={totalRating} readOnly={true} style={{
        maxWidth: 85
      }} itemStyles={starStyles} /><span className="text-sm text-light ml-2"><span className="font-medium">{totalRating}</span>/5 ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})</span></div>;
    $[23] = hasReviews;
    $[24] = reviewCount;
    $[25] = totalRating;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <div className="mb-4">{t12}{t13}</div>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="border-b border-gray-8" />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t11 || $[31] !== t14) {
    t16 = <div className={t11}>{t14}{t15}</div>;
    $[30] = t11;
    $[31] = t14;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FontAwesomeIcon icon={faPenToSquare} className="text-sm me-2 text-light group-hover:text-primary" />;
    t18 = <span className="text-sm underline">Write a Review</span>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t17 = $[33];
    t18 = $[34];
  }
  const t19 = `text-xs ms-2 text-light transition-transform duration-300 ${addReviewFormOpen ? "rotate-90" : ""}`;
  let t20;
  if ($[35] !== t19) {
    t20 = <FontAwesomeIcon icon={faChevronRight} className={t19} />;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== handleAddReviewClick || $[38] !== t20) {
    t21 = <button className="group flex items-center py-2 mb-4 -ml-1" onClick={handleAddReviewClick}>{t17}{t18}{t20}</button>;
    $[37] = handleAddReviewClick;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== addReviewFormOpen || $[41] !== closeReviewAddForm || $[42] !== handleAddReviewSubmit || $[43] !== handleInputChange || $[44] !== hasReviewErrors || $[45] !== isPending || $[46] !== newReview || $[47] !== newReviewScore || $[48] !== reviewErrors || $[49] !== sub_product_name || $[50] !== sub_product_thumb) {
    t22 = addReviewFormOpen && <div className={`grid overflow-hidden transition-all duration-300 ease-in-out
                    ${addReviewFormOpen ? "grid-rows-[1fr] opacity-100 mt-3 mb-24" : "grid-rows-[0fr] opacity-0 mb-2 md:mb-8"}`}><div className="overflow-hidden"><div className="flex flex-col md:flex-row items-start gap-4 md:gap-6"><div className="hidden md:block overlay-block bg-white p-2.5"><Image className="" src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${sub_product_thumb}`} alt={sub_product_name} placeholder="blur" blurDataURL="iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPcIgkAAYcAz3yXgHoAAAAASUVORK5CYII=" width={150} height={150} /></div><div className="w-full"><div className="flex items-center mb-4 md:mb-6"><div className="font-medium text-base text-primary me-1">Review:</div><div className="font-medium text-base capitalize">{sub_product_name}</div></div><form action={handleAddReviewSubmit}><FormError addClasses="text-xs font-medium">{reviewErrors.general}</FormError><div className="mb-6 md:mb-8"><FormError>{reviewErrors.review_score}</FormError><div className="text-xs mb-3"><span className="text-xs me-1 font-medium text-light">Your Rating</span><span className="text-3xs text-light-3">(required)</span></div><div className="flex items-center"><Rating value={newReviewScore} onChange={setNewReviewScore} style={{
                    maxWidth: 150
                  }} itemStyles={starStyles} /><div className="text-base md:text-sm text-light italic ml-4 flex items-center">{newReviewScore > 0 && <>{newReviewScore} stars</>}</div></div></div><div className="flex flex-col md:flex-row items-center w-full justify-between gap-4 md:gap-6 mb-4 md:mb-6"><div className="w-full"><FormError>{reviewErrors.user_alias}</FormError><div className="form-group"><input id="user_alias" type="text" name="user_alias" value={newReview.user_alias || ""} onChange={handleInputChange} className="form-input peer" placeholder=" " required={true} /><label htmlFor="user_alias" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">User Alias <span className="text-primary">*</span></label></div></div><div className="w-full"><FormError>{reviewErrors.review_title}</FormError><div className="form-group"><input id="review_title" type="text" name="review_title" value={newReview.review_title || ""} onChange={handleInputChange} className="form-input peer" placeholder=" " required={true} /><label htmlFor="review_title" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Review Title <span className="text-primary">*</span></label></div></div><div className="w-full"><FormError>{reviewErrors.email}</FormError><div className="form-group"><input id="user_email" type="email" name="email" value={newReview.email || ""} onChange={handleInputChange} className="form-input peer" placeholder=" " required={true} /><label htmlFor="user_email" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Email <span className="text-primary">*</span></label></div></div></div><div className="mb-6 md:mb-8"><FormError>{reviewErrors.review}</FormError><div className="form-group"><textarea id="review" name="review" onChange={handleInputChange} value={newReview.review || ""} className="form-input peer min-h-[120px]" placeholder=" " required={true} /><label htmlFor="review" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Your Review <span className="text-primary">*</span></label></div></div><FormError addClasses="text-xs font-medium mb-3">{hasReviewErrors && "Please correct the errors above."}</FormError><div className="flex flex-col sm:flex-row justify-start gap-3 sm:gap-4 mb-4 w-full sm:w-auto"><input type="submit" value="Add Review" disabled={isPending} className={`btn btn-sm btn-primary w-full sm:w-auto py-3 sm:py-2 ${isPending ? "text-black" : ""}`} /><button onClick={closeReviewAddForm} type="button" disabled={isPending} className={`text-sm sm:text-2xs py-3 sm:py-0 underline transition-colors duration-200 ${isPending ? "text-black" : "text-light hover:text-primary"}`}>Cancel</button></div><div className="text-light text-3xs"><p className="text-3xs mb-2"><sup className="text-primary">*</sup> Your email will not be published. We might email you about this submission if we have questions or concerns about the content. Your review will be moderated by our staff and may take a few days to be published on the product page.</p><RecaptchaDisclaimer addClasses="text-3xs" /></div></form></div></div></div></div>;
    $[40] = addReviewFormOpen;
    $[41] = closeReviewAddForm;
    $[42] = handleAddReviewSubmit;
    $[43] = handleInputChange;
    $[44] = hasReviewErrors;
    $[45] = isPending;
    $[46] = newReview;
    $[47] = newReviewScore;
    $[48] = reviewErrors;
    $[49] = sub_product_name;
    $[50] = sub_product_thumb;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== handleVote || $[53] !== helpfulCounts || $[54] !== reviews || $[55] !== votingReviewId) {
    t23 = <div className="">{reviews && reviews.length > 0 ? <>{reviews.map((review_0, index) => <div key={index} className="texts-xs border-b-1 border-gray-5 pb-4 mb-4 lg:pb-5 lg:mb-5"><div className="grid grdi-cols-2 lg:grid-cols-4"><div className="lg:col-span-2 grid grid-cols-subgrid grid-cols-1 lg:grid-cols-2"><div className="mb-1"><div className="text-md font-extralight mb-2">{review_0.user_alias}</div><div className="text-2xs text-light"><ReviewTime timestamp={review_0.added_ts} /></div></div><div className="mb-6"><div className="flex items-center"><Rating value={review_0.review_score} readOnly={true} style={{
                    maxWidth: 80
                  }} itemStyles={starStyles} /><div className="ml-1 text-xs text-light"><span className="font-semibold">{review_0.review_score}</span>/5</div></div></div></div><div className="lg:col-span-2"><div className="text-xs font-medium capitalize mb-3">{review_0.review_title}</div><p className="text-xs">{review_0.review}</p><div className="flex items-center space-x-3 mt-4"><button type="button" onClick={() => handleVote(review_0.review_id, "helpful")} disabled={votingReviewId === review_0.review_id} className={`text-2xs pr-3 border-r-1 border-gray-20 hover:text-primary transition-colors ${votingReviewId === review_0.review_id ? "opacity-50" : ""}`}>{(helpfulCounts[review_0.review_id]?.helpful_count || 0) > 0 && <span className="mr-1">({helpfulCounts[review_0.review_id]?.helpful_count})</span>}helpful<FontAwesomeIcon icon={faThumbsUp} className="text-2xs ml-1 text-light" /></button><button type="button" onClick={() => handleVote(review_0.review_id, "not_helpful")} disabled={votingReviewId === review_0.review_id} className={`text-2xs hover:text-primary transition-colors ${votingReviewId === review_0.review_id ? "opacity-50" : ""}`}>{(helpfulCounts[review_0.review_id]?.not_helpful_count || 0) > 0 && <span className="mr-1">({helpfulCounts[review_0.review_id]?.not_helpful_count})</span>}not helpful<FontAwesomeIcon icon={faThumbsDown} className="text-2xs ml-1 text-light" /></button></div></div></div></div>)}</> : <div className="text-center pb-12"><div className="flex justify-center mb-4"><Rating value={0} readOnly={true} style={{
            maxWidth: 100
          }} itemStyles={starStyles} /></div><div className="text-sm font-medium">There are no reviews of this product yet.</div></div>}</div>;
    $[52] = handleVote;
    $[53] = helpfulCounts;
    $[54] = reviews;
    $[55] = votingReviewId;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t10 || $[58] !== t16 || $[59] !== t21 || $[60] !== t22 || $[61] !== t23) {
    t24 = <div className={t10}>{t16}{t21}{t22}{t23}</div>;
    $[57] = t10;
    $[58] = t16;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  return t24;
};
export default Reviews;